import { Fragment, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Globe } from "lucide-react";
import { Logo, Wordmark, Txt } from "./Brand";
import { useSite } from "@/context/SiteContext";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/publications", label: "Research" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  useEffect(() => {
    if (!open) return;
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  return (
    <header role="banner" className={`sticky top-0 z-50 bg-white ${scrolled ? "shadow-[0_6px_24px_rgb(var(--irid-navy)/0.10)]" : ""}`} style={{ transition: "box-shadow 200ms ease" }}>
      <div className="container-irid flex h-[84px] items-center justify-between gap-6 md:h-[96px]">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <span className="block md:hidden"><Logo size={56} testid="header-logo-mobile" /></span>
          <span className="hidden md:block"><Logo size={80} testid="header-logo" /></span>
          <Wordmark />
        </Link>
        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) => `group relative block px-4 py-3 font-heading text-[15px] font-semibold ${isActive ? "text-irid-blue" : "text-irid-navy hover:text-irid-blue"}`}>
                  {({ isActive }) => (<>
                    {n.label}
                    <span aria-hidden="true" className={`absolute inset-x-4 bottom-1 h-[3px] rounded bg-[image:var(--irid-growth)] ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} style={{ transition: "opacity 180ms ease" }} />
                  </>)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/contact" className="btn-primary hidden sm:inline-flex" data-testid="header-contact-cta">Contact IRID</Link>
          <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-irid-navy hover:bg-irid-mist lg:hidden"
            aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? "Close menu" : "Open menu"} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>
      {open && (
        <div id="mobile-nav" className="on-dark fixed inset-x-0 bottom-0 top-[84px] z-50 overflow-y-auto bg-irid-navy lg:hidden" data-testid="mobile-nav-panel">
          <nav aria-label="Mobile primary" className="container-irid py-8">
            <ul className="space-y-1">
              {[{ to: "/", label: "Home" }, ...NAV, { to: "/leadership", label: "Leadership" }].map((n) => (
                <li key={n.to}>
                  <NavLink to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) => `block border-b border-white/10 py-4 font-heading text-xl font-semibold ${isActive ? "text-irid-gold" : "text-white"}`}>{n.label}</NavLink>
                </li>
              ))}
            </ul>
            <Link to="/contact?form=consultancy" className="btn-highlight mt-8 w-full" data-testid="mobile-nav-consult-cta">Request a Consultation</Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function ContactList({ dark = false, prefix = "contact" }) {
  const { settings: s } = useSite();
  if (!s) return null;
  const cls = dark ? "text-irid-mist" : "text-irid-ink";
  const row = (Icon, content, testid) => (
    <li className={`flex items-start gap-3 ${cls}`} data-testid={testid}>
      <Icon aria-hidden="true" className={`mt-1 h-4 w-4 shrink-0 ${dark ? "text-irid-lime" : "text-irid-blue"}`} />
      <span>{content}</span>
    </li>
  );
  const link = (href, text) => (href ? <a href={href} className={dark ? "underline hover:text-irid-gold" : "irid-link"}>{text}</a> : <Txt>{text}</Txt>);
  return (
    <ul className="space-y-3 text-[15px]">
      {row(MapPin, <Txt>{s.address}</Txt>, `${prefix}-address`)}
      {(s.telephones || []).map((t, i) => <Fragment key={t.href}>{row(Phone, link(t.href, t.display), `${prefix}-telephone-${i + 1}`)}</Fragment>)}
      {row(Mail, link(s.email_href, s.email), `${prefix}-email`)}
      {row(Globe, <span>{s.website}</span>, `${prefix}-website`)}
    </ul>
  );
}

export function Footer() {
  const { services } = useSite();
  return (
    <footer role="contentinfo" className="on-dark relative bg-irid-navy text-white" data-testid="site-footer">
      <div className="growth-band" aria-hidden="true" />
      <div className="container-irid grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Link to="/" className="inline-flex rounded-2xl bg-white p-4" data-testid="footer-logo-link"><Logo size={140} testid="footer-logo" /></Link>
          <p className="mt-5 max-w-xs font-heading text-sm font-semibold uppercase tracking-[0.08em] text-white">The Institute for Research, Innovation and Development</p>
        </div>
        <div className="lg:col-span-3">
          <h2 className="mb-5 font-heading text-base font-bold text-irid-gold">Contact</h2>
          <ContactList dark prefix="footer-contact" />
        </div>
        <div className="lg:col-span-3">
          <h2 className="mb-5 font-heading text-base font-bold text-irid-gold">Services</h2>
          <ul className="space-y-3 text-[15px]">
            {services.map((s) => <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-irid-mist hover:text-irid-gold">{s.title}</Link></li>)}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <h2 className="mb-5 font-heading text-base font-bold text-irid-gold">Quick links</h2>
          <ul className="space-y-3 text-[15px]">
            {[["/about", "About IRID"], ["/leadership", "Leadership"], ["/publications", "Research library"], ["/news", "News and insights"], ["/contact", "Contact"], ["/privacy", "Privacy notice"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="text-irid-mist hover:text-irid-gold" data-testid={`footer-link-${to.slice(1)}`}>{l}</Link></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-irid flex flex-col gap-3 py-6 text-sm text-irid-mist sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} The Institute for Research, Innovation and Development. Monrovia, Liberia.</p>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-irid-gold">Privacy</Link>
            <Link to="/admin/login" className="hover:text-irid-gold" data-testid="footer-staff-login">Staff login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function CtaBand({ title = "Work with IRID", text, cta = "Request a Consultation", to = "/contact?form=consultancy", secondary = { to: "/contact?form=partnership", label: "Explore partnership" } }) {
  const { settings } = useSite();
  return (
    <section className="on-dark relative overflow-hidden bg-irid-navy text-white" data-testid="cta-band">
      <svg aria-hidden="true" viewBox="0 0 800 300" className="pointer-events-none absolute -left-20 top-0 h-full w-[700px] opacity-25" fill="none">
        <path d="M0 260 C 200 80, 480 40, 820 120" stroke="rgb(var(--irid-teal))" strokeWidth="22" strokeLinecap="round" />
        <path d="M0 300 C 240 160, 520 130, 820 190" stroke="rgb(var(--irid-lime))" strokeWidth="10" strokeLinecap="round" />
      </svg>
      <div className="container-irid relative flex flex-col items-start gap-8 py-16 md:flex-row md:items-center md:justify-between md:py-20">
        <div className="max-w-2xl">
          <span className="eyebrow mb-3">Work with us</span>
          <h2 className="mt-2 text-white">{title}</h2>
          <p className="mt-4 text-lg text-white/90">{text || settings?.work_with_intro}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to={to} className="btn-highlight" data-testid="cta-band-primary">{cta}</Link>
          {secondary && <Link to={secondary.to} className="btn-secondary-dark" data-testid="cta-band-secondary">{secondary.label}</Link>}
        </div>
      </div>
    </section>
  );
}
